import { loadSource as decodeSource } from '@/core/image'
import { persistNewEdit, takeSharedInboxFiles } from '@/core/persistence'
import { trackUsage } from '@/core/reporting/runtime'
import { ReportingStage, UsageEvent } from '@/core/reporting/types'
import {
  SHARE_INBOX_QUERY_PARAM,
  SHARE_INBOX_QUERY_VALUE,
} from '@/core/shareTarget'
import { useEditSession } from '@/core/store/editSession'
import { enterCollage, fillSelectedCollageFiles } from '@/tools/collage/actions'
import { surfaceError } from '@/core/reporting/surfaceError'

class EmptySharedInboxError extends Error {
  constructor() {
    super('No shared photos were found. Try sharing them again.')
    this.name = 'EmptySharedInboxError'
  }
}

function isSharedInboxLaunch(url: URL): boolean {
  return url.searchParams.get(SHARE_INBOX_QUERY_PARAM) === SHARE_INBOX_QUERY_VALUE
}

function stripSharedInboxQuery(url: URL): void {
  url.searchParams.delete(SHARE_INBOX_QUERY_PARAM)
  window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`)
}

async function loadSharedFiles(files: readonly File[]): Promise<void> {
  const first = files[0]
  if (!first) throw new EmptySharedInboxError()

  useEditSession.getState().setIsLoading(true)
  try {
    const source = await decodeSource(first)
    useEditSession.getState().loadSource(source)
  } finally {
    useEditSession.getState().setIsLoading(false)
  }

  if (files.length > 1) {
    enterCollage()
    await fillSelectedCollageFiles(files.slice(1))
  }
  trackUsage(UsageEvent.SharedIn, { files: files.length })
  await persistNewEdit()
}

export async function handleSharedInboxLaunch(): Promise<boolean> {
  const url = new URL(window.location.href)
  if (!isSharedInboxLaunch(url)) return false
  stripSharedInboxQuery(url)

  try {
    await loadSharedFiles(await takeSharedInboxFiles())
  } catch (error) {
    useEditSession.getState().setIsLoading(false)
    surfaceError(error, 'Could not open photos shared into the app.', { stage: ReportingStage.SourceLoad })
  }
  return true
}
